
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';

interface ContentBlock {
  type: 'text' | 'list' | 'benefits' | 'process' | 'cta';
  title?: string;
  content?: string;
  items?: string[];
  benefits?: Array<{ title: string; description: string; icon?: React.ComponentType<any> }>;
  process?: Array<{ step: number; title: string; description: string }>;
  ctaText?: string;
  ctaLink?: string;
  variant?: 'default' | 'highlighted' | 'success';
}

interface ValorisationContentSectionProps {
  title: string;
  subtitle?: string;
  blocks: ContentBlock[];
  className?: string;
}

const ValorisationContentSection: React.FC<ValorisationContentSectionProps> = ({
  title,
  subtitle,
  blocks,
  className = ""
}) => {
  const renderBlock = (block: ContentBlock, index: number) => {
    const baseClasses = "mb-8";
    const variantClasses = {
      default: "bg-white",
      highlighted: "bg-fabre-lightbeige",
      success: "bg-green-50 border-green-200"
    };

    switch (block.type) {
      case 'text':
        return (
          <div key={index} className={`${baseClasses} ${variantClasses[block.variant || 'default']} ${block.variant !== 'default' ? 'p-6 rounded-lg' : ''}`}>
            {block.title && (
              <h3 className="text-2xl font-heading font-bold text-fabre-gray mb-4">
                {block.title}
              </h3>
            )}
            {block.content && (
              <p className="text-gray-700 leading-relaxed text-lg">
                {block.content}
              </p>
            )}
          </div>
        );

      case 'list':
        return (
          <div key={index} className={`${baseClasses} ${variantClasses[block.variant || 'default']} ${block.variant !== 'default' ? 'p-6 rounded-lg' : ''}`}>
            {block.title && (
              <h3 className="text-2xl font-heading font-bold text-fabre-gray mb-6">
                {block.title}
              </h3>
            )}
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {block.items?.map((item, itemIndex) => (
                <li key={itemIndex} className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-fabre-green mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        );

      case 'benefits':
        return (
          <div key={index} className={`${baseClasses} ${variantClasses[block.variant || 'highlighted']} p-8 rounded-xl`}>
            {block.title && (
              <h3 className="text-2xl font-heading font-bold text-fabre-gray mb-8 text-center">
                {block.title}
              </h3>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {block.benefits?.map((benefit, benefitIndex) => {
                const IconComponent = benefit.icon;
                return (
                  <Card key={benefitIndex} className="border-fabre-green/20 hover:border-fabre-green transition-colors">
                    <CardHeader className="pb-3">
                      {IconComponent && (
                        <div className="w-12 h-12 bg-fabre-green rounded-lg flex items-center justify-center mb-3">
                          <IconComponent className="h-6 w-6 text-white" />
                        </div>
                      )}
                      <CardTitle className="text-lg text-fabre-gray">{benefit.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600">{benefit.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );

      case 'process':
        return (
          <div key={index} className={`${baseClasses} ${variantClasses[block.variant || 'default']} ${block.variant !== 'default' ? 'p-6 rounded-lg' : ''}`}>
            {block.title && (
              <h3 className="text-2xl font-heading font-bold text-fabre-gray mb-8 text-center">
                {block.title}
              </h3>
            )}
            <div className="space-y-6">
              {block.process?.map((step, stepIndex) => (
                <div key={stepIndex} className="flex items-start">
                  <div className="flex items-center justify-center bg-fabre-green text-white rounded-full w-10 h-10 mr-4 flex-shrink-0 font-bold">
                    {step.step}
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-fabre-gray mb-2">{step.title}</h4>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'cta':
        return (
          <div key={index} className={`${baseClasses} text-center ${variantClasses[block.variant || 'highlighted']} p-8 rounded-xl`}>
            {block.title && (
              <h3 className="text-2xl font-heading font-bold text-fabre-gray mb-4">
                {block.title}
              </h3>
            )}
            {block.content && (
              <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
                {block.content}
              </p>
            )}
            {block.ctaText && block.ctaLink && (
              <Button asChild size="lg" className="bg-fabre-green hover:bg-fabre-darkgreen">
                <Link to={block.ctaLink}>
                  {block.ctaText} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <section className={`py-16 ${className}`}>
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-fabre-gray mb-4">
            {title}
          </h2>
          {subtitle && (
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {subtitle}
            </p>
          )}
        </div>

        <div className="space-y-0">
          {blocks.map((block, index) => renderBlock(block, index))}
        </div>
      </div>
    </section>
  );
};

export default ValorisationContentSection;
